import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { ChevronRight, Home } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { appelsOffresService, societesService, collaborateursService, servicesService } from "@/services";

interface BreadcrumbItemType {
  label: string;
  path: string;
  isActive?: boolean;
}

interface AppBreadcrumbProps {
  embedded?: boolean;
}

const AppBreadcrumb = ({ embedded }: AppBreadcrumbProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [breadcrumbItems, setBreadcrumbItems] = useState<BreadcrumbItemType[]>([]);
  const [dynamicData, setDynamicData] = useState<Record<string, string>>({});

  // Route configurations with labels
  const routeConfig: Record<string, string> = {
    '/home': 'Accueil',
    '/appels-offres': 'Appels d\'Offres',
    '/appels-offres/nouveau': 'Nouvel Appel d\'Offres',
    '/import-cv': 'Import CV',
    '/analyses': 'Analyses',
    '/ranking-societes': 'Ranking Sociétés',
    '/planning-ao': 'Planning analyse',
    '/societes': 'Sociétés',
    '/services': 'Services',
    '/collaborateurs': 'Collaborateurs',
    '/parametres': 'Paramètres',
    '/architecture': 'Architecture',
  };

  // Fetch dynamic data for specific routes
  useEffect(() => {
    const fetchDynamicData = async () => {
      const pathSegments = location.pathname.split('/').filter(Boolean);

      // Handle appels-offres detail page
      if (pathSegments[0] === 'appels-offres' && pathSegments[1] && pathSegments[1] !== 'nouveau') {
        try {
          const ao = await appelsOffresService.getById(pathSegments[1]);
          if (ao) {
            setDynamicData(prev => ({
              ...prev,
              [`/appels-offres/${pathSegments[1]}`]: ao.titre
            }));
          }
        } catch (error) {
          console.error('Error fetching AO data:', error);
        }
      }

      // Handle societes detail page
      if (pathSegments[0] === 'societes' && pathSegments[1]) {
        try {
          const societe = await societesService.getById(pathSegments[1]);
          if (societe) {
            setDynamicData(prev => ({
              ...prev,
              [`/societes/${pathSegments[1]}`]: societe.nom
            }));
          }
        } catch (error) {
          console.error('Error fetching Societe data:', error);
        }
      }

      // Handle collaborateurs detail page
      if (pathSegments[0] === 'collaborateurs' && pathSegments[1]) {
        try {
          const collaborateur = await collaborateursService.getById(pathSegments[1]);
          if (collaborateur) {
            setDynamicData(prev => ({
              ...prev,
              [`/collaborateurs/${pathSegments[1]}`]: collaborateur.nom
            }));
          }
        } catch (error) {
          console.error('Error fetching Collaborateur data:', error);
        }
      }

      // Handle services detail page
      if (pathSegments[0] === 'services' && pathSegments[1]) {
        try {
          const service = await servicesService.getById(pathSegments[1]);
          if (service) {
            setDynamicData(prev => ({
              ...prev,
              [`/services/${pathSegments[1]}`]: service.nom
            }));
          }
        } catch (error) {
          console.error('Error fetching Service data:', error);
        }
      }
    };

    fetchDynamicData();
  }, [location.pathname]);

  // Generate breadcrumb items based on current path
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const normalizedSegments =
      pathSegments[0] === "home" ? pathSegments.slice(1) : pathSegments;
    const items: BreadcrumbItemType[] = [];

    items.push({
      label: 'Accueil',
      path: '/home',
      isActive: normalizedSegments.length === 0
    });

    let currentPath = pathSegments[0] === "home" ? "/home" : "";
    normalizedSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === normalizedSegments.length - 1;
      
      // Get label from config or dynamic data
      let label = routeConfig[currentPath] || dynamicData[currentPath];
      
      // If no label found, try to format the segment nicely
      if (!label) {
        if (segment.length > 8 && segment.includes('-')) {
          // Likely a UUID, show as "Détail"
          label = 'Détail';
        } else {
          // Capitalize and replace dashes with spaces
          label = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
        }
      }

      items.push({
        label,
        path: currentPath,
        isActive: isLast
      });
    });

    setBreadcrumbItems(items);
  }, [location.pathname, dynamicData]);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  if (location.pathname === '/auth' || location.pathname === "/home" || breadcrumbItems.length <= 1) {
    return null;
  }

  const wrapperClassName = embedded
    ? "flex-1 min-w-0"
    : "border-b border-border bg-muted/10 px-4 py-2";

  return (
    <div className={wrapperClassName}>
      <Breadcrumb>
        <BreadcrumbList>
          {breadcrumbItems.map((item, index) => (
            <div key={item.path} className="flex items-center">
              <BreadcrumbItem>
                {item.isActive ? (
                  <BreadcrumbPage className="font-medium text-foreground">
                    {index === 0 && <Home className="h-4 w-4 mr-1" />}
                    {item.label}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink 
                    onClick={() => handleNavigation(item.path)}
                    className="flex items-center text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                  >
                    {index === 0 && <Home className="h-4 w-4 mr-1" />}
                    {item.label}
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              
              {!item.isActive && index < breadcrumbItems.length - 1 && (
                <BreadcrumbSeparator>
                  <ChevronRight className="h-4 w-4" />
                </BreadcrumbSeparator>
              )}
            </div>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
};

export default AppBreadcrumb;
